/**
 * True when an error from a campaign fetch should be presented to the user as
 * "this campaign isn't available." Covers both ways a cross-brand campaign
 * read can be rejected:
 *
 *  - 404 → typed `ClientError` (`status: 404`) from the campaign data-layer
 *    brand filter when the caller has wildcard ACL.
 *  - 403 → `AxiosError` (`response.status: 403`) from the framework entity
 *    ACL when a brand-scoped caller doesn't include the campaign's owning
 *    brand in their ACL.
 *
 * @param err The error thrown by an OlympusClient campaign fetch.
 */
export const isCampaignUnavailable = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false

  // ClientError carries a bare `status`. We only match 404 — a bare
  // `status: 403` would be an auth or rate-limit error from some future
  // typed code and shouldn't be surfaced as "Campaign not found".
  if ((err as { status?: unknown }).status === 404) return true

  // AxiosError carries `response.status`. We only match 403 — that's the
  // framework entity ACL shape. A 404 from this endpoint comes through as
  // a ClientError (handled above), not an AxiosError.
  if ((err as { response?: { status?: unknown } }).response?.status === 403) {
    return true
  }

  return false
}
